import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { generateClient } from "aws-amplify/api"
import {
    MDBBtn,
    MDBCard,
    MDBCardBody,
    MDBCardTitle,
    MDBCol,
    MDBRow,
    MDBTable,
    MDBTableBody,
    MDBTableHead,
    MDBSelect,
    MDBInput,
    MDBIcon,
    MDBBadge,
    MDBSpinner,
    MDBTextArea
} from "mdb-react-ui-kit"
import { Link } from "react-router-dom"
import { getLocalizedText } from "../../utils/localizedText"
import { captureError } from "../../utils/sentryHelpers"

const client = generateClient()

export default function CustomBoothRequests() {
    const { t, i18n } = useTranslation()

    const [events, setEvents] = useState([])
    const [requests, setRequests] = useState([])
    const [filteredRequests, setFilteredRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [filters, setFilters] = useState({})
    const [editingNotes, setEditingNotes] = useState({})
    const [deletingRequestId, setDeletingRequestId] = useState(null)

    const getEvents = async () => {
        try {
            const events = await client.graphql({
                query: /* GraphQL */ `
                query ListEvents {
                    listEvents(filter: { type: { eq: "fair" } }) {
                        items {
                            id
                            type
                            name {
                                text
                                language
                            }
                            startDate
                            endDate
                            status
                        }
                    }
                }
                `,
                variables: {
                    filter: {
                        startDate: {
                            ge: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().substring(0, 10)
                        },
                        type: { eq: "fair" }
                    }
                }
            });
            setEvents(events.data.listEvents.items.filter(event => event.status === "active"));
            return events.data.listEvents.items;
        } catch (error) {
            console.log("error on fetching events", error);
            captureError(error, {
                feature: 'custom-booth-requests',
                operation: 'fetch-events',
                consoleMessage: 'error on fetching events'
            });
        }
    }

    const getCustomRequests = async (eventId) => {
        try {
            setLoading(true);
            let allRequests = [];
            let nextToken = null;

            do {
                const response = await client.graphql({
                    query: /* GraphQL */ `
                    query ListBoothCustomRequests(
                        $filter: ModelBoothCustomRequestFilterInput
                        $limit: Int
                        $nextToken: String
                    ) {
                        listBoothCustomRequests(filter: $filter, limit: $limit, nextToken: $nextToken) {
                            items {
                                id
                                requestText
                                status
                                adminNotes
                                createdByUserId
                                owner
                                createdAt
                                updatedAt
                                bookedBooth {
                                    id
                                    boothNumber
                                    company {
                                        id
                                        name
                                    }
                                    event {
                                        id
                                        name {
                                            language
                                            text
                                        }
                                    }
                                    orderLineItem {
                                        order {
                                            user {
                                                id
                                                firstName
                                                lastName
                                                email
                                            }
                                        }
                                    }
                                }
                            }
                            nextToken
                        }
                    }
                    `,
                    variables: {
                        limit: 500,
                        nextToken: nextToken
                    }
                });

                allRequests = [...allRequests, ...response.data.listBoothCustomRequests.items];
                nextToken = response.data.listBoothCustomRequests.nextToken;
            } while (nextToken);

            // Filter by event if specified
            if (eventId) {
                allRequests = allRequests.filter(req => req.bookedBooth?.event?.id === eventId);
            }

            // Sort by creation date (newest first)
            allRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setRequests(allRequests);
            setFilteredRequests(allRequests);
            setLoading(false);
        } catch (error) {
            console.error("error fetching custom requests", error);
            captureError(error, {
                feature: 'custom-booth-requests',
                operation: 'fetch-custom-requests',
                consoleMessage: 'error fetching custom requests',
                context: { eventId }
            });
            setLoading(false);
        }
    }

    const handleEventSelection = (eventId) => {
        setFilters({ ...filters, eventId });
        getCustomRequests(eventId);
    };

    const updateRequestStatus = async (requestId, newStatus) => {
        try {
            await client.graphql({
                query: /* GraphQL */ `
                mutation UpdateBoothCustomRequest(
                    $input: UpdateBoothCustomRequestInput!
                ) {
                    updateBoothCustomRequest(input: $input) {
                        id
                        status
                    }
                }
                `,
                variables: {
                    input: {
                        id: requestId,
                        status: newStatus
                    }
                }
            });

            // Update local state
            const updatedRequests = requests.map(req =>
                req.id === requestId ? { ...req, status: newStatus } : req
            );
            setRequests(updatedRequests);
            setFilteredRequests(updatedRequests);
        } catch (error) {
            console.error("error updating request status", error);
            captureError(error, {
                feature: 'custom-booth-requests',
                operation: 'update-request-status',
                consoleMessage: 'error updating request status',
                context: { requestId, newStatus }
            });
        }
    };

    const updateAdminNotes = async (requestId, notes) => {
        try {
            await client.graphql({
                query: /* GraphQL */ `
                mutation UpdateBoothCustomRequest(
                    $input: UpdateBoothCustomRequestInput!
                ) {
                    updateBoothCustomRequest(input: $input) {
                        id
                        adminNotes
                    }
                }
                `,
                variables: {
                    input: {
                        id: requestId,
                        adminNotes: notes
                    }
                }
            });

            // Update local state
            const updatedRequests = requests.map(req =>
                req.id === requestId ? { ...req, adminNotes: notes } : req
            );
            setRequests(updatedRequests);
            setFilteredRequests(updatedRequests);

            // Clear editing state
            const newEditingNotes = { ...editingNotes };
            delete newEditingNotes[requestId];
            setEditingNotes(newEditingNotes);
        } catch (error) {
            console.error("error updating admin notes", error);
            captureError(error, {
                feature: 'custom-booth-requests',
                operation: 'update-admin-notes',
                consoleMessage: 'error updating admin notes',
                context: { requestId }
            });
        }
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        if (value) {
            const filtered = requests.filter(req =>
                (req.bookedBooth?.company?.name || "").toLowerCase().includes(value.toLowerCase()) ||
                (req.requestText || "").toLowerCase().includes(value.toLowerCase())
            );
            setFilteredRequests(filtered);
        } else {
            setFilteredRequests(requests);
        }
    };

    const getStatusBadgeColor = (status) => {
        switch(status) {
            case "in_progress":
                return "warning";
            case "offer_sent":
                return "info";
            case "rejected":
                return "danger";
            default:
                return "secondary";
        }
    };

    const getStatusText = (status) => {
        switch(status) {
            case "in_progress":
                return t("In Progress");
            case "offer_sent":
                return t("Offer Sent");
            case "rejected":
                return t("Rejected");
            default:
                return status;
        }
    };

    const deleteRequest = async (requestId) => {
        if (!window.confirm(t("Are you sure you want to delete this request?"))) {
            return;
        }

        try {
            setDeletingRequestId(requestId);

            await client.graphql({
                query: /* GraphQL */ `
                mutation DeleteBoothCustomRequest(
                    $input: DeleteBoothCustomRequestInput!
                ) {
                    deleteBoothCustomRequest(input: $input) {
                        id
                    }
                }
                `,
                variables: {
                    input: {
                        id: requestId
                    }
                }
            });

            // Update local state
            const updatedRequests = requests.filter(req => req.id !== requestId);
            setRequests(updatedRequests);
            setFilteredRequests(updatedRequests.filter(req =>
                !searchTerm ||
                (req.bookedBooth?.company?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (req.requestText || "").toLowerCase().includes(searchTerm.toLowerCase())
            ));
        } catch (error) {
            console.error("error deleting request", error);
            captureError(error, {
                feature: 'custom-booth-requests',
                operation: 'delete-request',
                consoleMessage: 'error deleting request',
                context: { requestId }
            });
            alert(t("Error deleting request"));
        } finally {
            setDeletingRequestId(null);
        }
    };

    useEffect(() => {
        getEvents();
    }, [])

    return (
        <>
        <MDBRow>
            <MDBCol>
                <MDBCard>
                    <MDBCardBody>
                        <MDBCardTitle>{t("Individual Booth Requests")}</MDBCardTitle>
                        {!filters.eventId ? (
                            <MDBCard className="text-center">
                                <MDBCardBody className="py-5">
                                    <MDBIcon fas icon="calendar-alt" size="3x" className="mb-3 text-primary" />
                                    <h4 className="mb-3">{t("Please Select an Event")}</h4>
                                    <p className="text-muted mb-4">{t("Select an event to view its custom booth requests")}</p>
                                    <div className="w-50 mx-auto">
                                        <MDBSelect
                                            label={t("Event")}
                                            onChange={event => handleEventSelection(event.value)}
                                            data={[
                                                { text: t("Select an event..."), value: "" },
                                                ...events.map(event => ({
                                                    text: getLocalizedText(event.name),
                                                    value: event.id
                                                }))
                                            ]}
                                        />
                                    </div>
                                </MDBCardBody>
                            </MDBCard>
                        ) : (
                            <>
                                <MDBRow className="mt-2 mt-xl-3">
                                    <MDBCol md="6">
                                        <MDBSelect
                                            label={t("Event")}
                                            onChange={event => handleEventSelection(event.value)}
                                            value={filters.eventId}
                                            data={[
                                                ...events.map(event => ({
                                                    text: getLocalizedText(event.name),
                                                    value: event.id
                                                }))
                                            ]}
                                        />
                                    </MDBCol>
                                    <MDBCol md="6">
                                        <MDBInput
                                            label={t("Search Company or Request")}
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => handleSearch(e.target.value)}
                                        />
                                    </MDBCol>
                                </MDBRow>

                                {loading ? (
                                    <div className="text-center my-5">
                                        <MDBSpinner />
                                    </div>
                                ) : filteredRequests.length === 0 ? (
                                    <div className="text-center my-5">
                                        <MDBIcon fas icon="inbox" size="3x" className="mb-3 text-muted" />
                                        <p className="text-muted">{t("No custom requests found for this event")}</p>
                                    </div>
                                ) : (
                                    <MDBTable align="middle" className="mt-3">
                                        <MDBTableHead>
                                            <tr>
                                                <th>{t("Company")}</th>
                                                <th>{t("Booth Number")}</th>
                                                <th>{t("Request")}</th>
                                                <th>{t("Status")}</th>
                                                <th>{t("Admin Notes")}</th>
                                                <th>{t("Date")}</th>
                                                <th>{t("Actions")}</th>
                                            </tr>
                                        </MDBTableHead>
                                        <MDBTableBody>
                                            {filteredRequests.map((request, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <Link to={`/admin/company/${request.bookedBooth?.company?.id}`} className="fw-bold">
                                                            {request.bookedBooth?.company?.name || "N/A"}
                                                        </Link>
                                                        <br />
                                                        <small className="text-muted">
                                                            {request.bookedBooth?.orderLineItem?.order?.user?.firstName} {request.bookedBooth?.orderLineItem?.order?.user?.lastName}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <Link to={`/mycompany/${request.bookedBooth?.company?.id}/edit-booth/${request.bookedBooth?.id}`}>
                                                            {request.bookedBooth?.boothNumber || t("Not assigned")}
                                                        </Link>
                                                    </td>
                                                    <td style={{maxWidth: "300px"}}>
                                                        {request.requestText}
                                                    </td>
                                                    <td>
                                                        <MDBSelect
                                                            data={[
                                                                { text: t("In Progress"), value: "in_progress" },
                                                                { text: t("Offer Sent"), value: "offer_sent" },
                                                                { text: t("Rejected"), value: "rejected" }
                                                            ]}
                                                            value={request.status}
                                                            onChange={(e) => updateRequestStatus(request.id, e.value)}
                                                            style={{minWidth: "150px"}}
                                                        />
                                                        <MDBBadge color={getStatusBadgeColor(request.status)} className="mt-2">
                                                            {getStatusText(request.status)}
                                                        </MDBBadge>
                                                    </td>
                                                    <td style={{maxWidth: "250px"}}>
                                                        {editingNotes[request.id] !== undefined ? (
                                                            <>
                                                                <MDBTextArea
                                                                    rows={3}
                                                                    value={editingNotes[request.id]}
                                                                    onChange={(e) => setEditingNotes({
                                                                        ...editingNotes,
                                                                        [request.id]: e.target.value
                                                                    })}
                                                                />
                                                                <div className="mt-2">
                                                                    <MDBBtn
                                                                        size="sm"
                                                                        color="primary"
                                                                        onClick={() => updateAdminNotes(request.id, editingNotes[request.id])}
                                                                    >
                                                                        <MDBIcon fas icon="save" className="me-1" />
                                                                        {t("Save")}
                                                                    </MDBBtn>
                                                                    <MDBBtn
                                                                        size="sm"
                                                                        color="secondary"
                                                                        outline
                                                                        className="ms-2"
                                                                        onClick={() => {
                                                                            const newEditingNotes = { ...editingNotes };
                                                                            delete newEditingNotes[request.id];
                                                                            setEditingNotes(newEditingNotes);
                                                                        }}
                                                                    >
                                                                        {t("Cancel")}
                                                                    </MDBBtn>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {request.adminNotes || <span className="text-muted">{t("No notes")}</span>}
                                                                <br />
                                                                <MDBBtn
                                                                    size="sm"
                                                                    color="tertiary"
                                                                    rippleColor="light"
                                                                    className="mt-2"
                                                                    onClick={() => setEditingNotes({
                                                                        ...editingNotes,
                                                                        [request.id]: request.adminNotes || ""
                                                                    })}
                                                                >
                                                                    <MDBIcon far icon="edit" className="me-1" />
                                                                    {t("Edit")}
                                                                </MDBBtn>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {new Date(request.createdAt).toLocaleString("de-DE")}
                                                    </td>
                                                    <td>
                                                        <MDBBtn
                                                            size="sm"
                                                            color="danger"
                                                            onClick={() => deleteRequest(request.id)}
                                                            disabled={deletingRequestId === request.id}
                                                        >
                                                            {deletingRequestId === request.id ? (
                                                                <MDBSpinner size="sm" />
                                                            ) : (
                                                                <>
                                                                    <MDBIcon fas icon="trash" className="me-1" />
                                                                    {t("Delete")}
                                                                </>
                                                            )}
                                                        </MDBBtn>
                                                    </td>
                                                </tr>
                                            ))}
                                        </MDBTableBody>
                                    </MDBTable>
                                )}
                            </>
                        )}
                    </MDBCardBody>
                </MDBCard>
            </MDBCol>
        </MDBRow>
        </>
    )
}
